// Calc block config on org form templates — form-local line math (not proposal format calcs).

import type { OrgFormTemplateBlock } from "./orgFormTemplateTypes.js";

export const ORG_FORM_CALC_AMOUNT_MODES = [
  "base_price",
  "fixed",
  "fixed_negative",
  "percent",
  "percent_negative",
  "total",
] as const;

export type OrgFormCalcAmountMode = (typeof ORG_FORM_CALC_AMOUNT_MODES)[number];

export const ORG_FORM_CALC_AMOUNT_MODE_LABELS: Record<OrgFormCalcAmountMode, string> = {
  base_price: "Base price",
  fixed: "Fixed",
  fixed_negative: "Fixed negative",
  percent: "Percent",
  percent_negative: "Percent negative",
  total: "Total",
};

export type OrgFormCalcConfig = {
  amount_mode: OrgFormCalcAmountMode;
  default_amount: number;
  visible_on_pdf: boolean;
  /** Percent modes: evaluated dollar amounts from these prior calc block ids. */
  basis_block_ids: string[];
};

export const DEFAULT_ORG_FORM_BASE_PRICE_CONFIG: OrgFormCalcConfig = {
  amount_mode: "base_price",
  default_amount: 10_000,
  visible_on_pdf: true,
  basis_block_ids: [],
};

export const ORG_FORM_DEFAULT_PERCENT_AMOUNT = 7;

export const DEFAULT_ORG_FORM_CALC_CONFIG: OrgFormCalcConfig = {
  amount_mode: "fixed",
  default_amount: 0,
  visible_on_pdf: true,
  basis_block_ids: [],
};

const LEGACY_MODE_MAP: Record<string, OrgFormCalcAmountMode> = {
  fixed: "fixed",
  percent: "percent",
};

function normalizeBasisIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === "string" && id.length > 0);
}

export function parseOrgFormCalcConfig(raw: unknown): OrgFormCalcConfig | { error: string } {
  if (!raw || typeof raw !== "object") return DEFAULT_ORG_FORM_CALC_CONFIG;
  const o = raw as Record<string, unknown>;

  let amountMode: OrgFormCalcAmountMode;
  if (typeof o.amount_mode === "string" && ORG_FORM_CALC_AMOUNT_MODES.includes(o.amount_mode as OrgFormCalcAmountMode)) {
    amountMode = o.amount_mode as OrgFormCalcAmountMode;
  } else if (typeof o.amount_mode === "string" && o.amount_mode in LEGACY_MODE_MAP) {
    amountMode = LEGACY_MODE_MAP[o.amount_mode];
  } else {
    amountMode = "fixed";
  }

  const defaultAmount =
    typeof o.default_amount === "number" ? o.default_amount : Number(o.default_amount);
  if (!Number.isFinite(defaultAmount) || defaultAmount < 0) {
    return { error: "calc_config.default_amount must be a non-negative number" };
  }
  if (
    (amountMode === "percent" || amountMode === "percent_negative") &&
    defaultAmount > 100
  ) {
    return { error: "calc_config percent default_amount cannot exceed 100" };
  }

  const visibleOnPdf =
    o.visible_on_pdf !== undefined
      ? o.visible_on_pdf !== false
      : o.visible_on_proposal !== false;

  return {
    amount_mode: amountMode,
    default_amount: defaultAmount,
    visible_on_pdf: visibleOnPdf,
    basis_block_ids: normalizeBasisIds(o.basis_block_ids),
  };
}

export function isOrgFormBasePriceBlock(block: OrgFormTemplateBlock): boolean {
  if (block.block_type !== "calc" || !block.calc_config) return false;
  const parsed = parseOrgFormCalcConfig(block.calc_config);
  return !("error" in parsed) && parsed.amount_mode === "base_price";
}

export function orgFormFilledBlocksHaveCalc(blocks: OrgFormTemplateBlock[]): boolean {
  return blocks.some((b) => b.block_type === "calc");
}

export function orgFormCalcModeNeedsInput(mode: OrgFormCalcAmountMode): boolean {
  return mode !== "total";
}

export function orgFormCalcModeUsesPercentInput(mode: OrgFormCalcAmountMode): boolean {
  return mode === "percent" || mode === "percent_negative";
}
